// « Pièce » — one invoice number, as the operator reads it (docs/PMS-LEDGER-UI-BRIEF.md §2.1).
//
// The Vega invoice (if Vega has one for this number), then every ledger entry carrying the number: the sale
// and each payment, with the tender, the date and the amount. The payments listed on top are read from the
// LEDGER's own bank entries, i.e. exactly what was posted. The Vega movements bearing the same number are shown
// apart and labelled as Vega's, because an `ARVE` movement carries a reservation number and only matches when
// the numbers happen to be equal.
//
// Honesty: a number Vega does not know says « Aucune facture Vega portant ce numéro » and still shows its
// entries (it never invents a link); a number with no entry says so; « n sur N » when the entries are capped;
// a missing right is « Droit manquant », a failed read « illisible », neither looks like an empty document.

import { useCallback, useEffect, useState } from 'react';
import {
  formatAmount,
  formatDay,
  journalLabel,
  paymentsOf,
  type DocumentDetail,
  type LedgerReader,
} from '@lemontaigu/fin-core';

const money = (cents: number | null): string => (cents === null ? '—' : formatAmount(cents));

export function DocumentView({ doc, onClose }: { doc: DocumentDetail; onClose: () => void }) {
  const inv = doc.invoice;
  const payments = paymentsOf(doc);
  return (
    <section className="fin-document" data-testid="fin-document" aria-label={`Pièce ${doc.piece}`}>
      <div className="fin-mouvements-head">
        <h4>Pièce {doc.piece}</h4>
        <button type="button" onClick={onClose}>Fermer</button>
      </div>

      {inv ? (
        <dl className="fin-facts" data-testid="fin-document-invoice">
          <div><dt>Facture Vega</dt><dd>{inv.facture}{inv.invoiceDate ? ` du ${formatDay(inv.invoiceDate)}` : ''}</dd></div>
          <div><dt>Client</dt><dd>{inv.name ?? '—'}{inv.clientRef ? ` (${inv.clientRef})` : ''}</dd></div>
          <div><dt>Total</dt><dd className="fin-num">{money(inv.totalCents)}</dd></div>
          <div><dt>Réglé</dt><dd className="fin-num">{money(inv.settledCents)}</dd></div>
          <div><dt>Arrhes déduites</dt><dd className="fin-num">{money(inv.depositAppliedCents)}</dd></div>
          <div><dt>Base TVA 5,5 %</dt><dd className="fin-num">{money(inv.base55Cents)}</dd></div>
          <div><dt>Base TVA 10 %</dt><dd className="fin-num">{money(inv.base10Cents)}</dd></div>
          <div><dt>Base TVA 20 %</dt><dd className="fin-num">{money(inv.base20Cents)}</dd></div>
          <div><dt>Base exonérée</dt><dd className="fin-num">{money(inv.baseExemptCents)}</dd></div>
        </dl>
      ) : (
        <p className="muted" data-testid="fin-document-no-invoice">Aucune facture Vega portant ce numéro.</p>
      )}

      <h5>Règlements (d’après le livre)</h5>
      {payments.length === 0 ? (
        <p className="muted" data-testid="fin-document-no-payment">Aucun règlement posté sous ce numéro.</p>
      ) : (
        <div className="pms-table-wrap">
          <table className="pms-table" data-testid="fin-document-payments">
            <thead>
              <tr><th>Date</th><th>Moyen de paiement</th><th className="fin-num">Montant</th></tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={`${p.entryId}|${p.account}|${p.amountCents}`}>
                  <td>{formatDay(p.date)}</td>
                  <td>{p.accountLabel} <span className="muted">({p.account})</span></td>
                  <td className="fin-num">{formatAmount(p.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h5>Écritures du livre</h5>
      {doc.entries.length === 0 ? (
        <p className="muted" data-testid="fin-document-no-entry">Aucune écriture du livre ne porte ce numéro.</p>
      ) : (
        <>
          {doc.entries.map((e) => (
            <div key={e.entryId} className="fin-entry" data-testid="fin-document-entry">
              <p className="fin-entry-head">
                <strong>{formatDay(e.entryDate)}</strong> · {journalLabel(e.journal)} · <span className="muted">{e.sourceId}</span>
              </p>
              <div className="pms-table-wrap">
                <table className="pms-table">
                  <thead>
                    <tr><th>Compte</th><th>Libellé</th><th>Client</th><th className="fin-num">Débit</th><th className="fin-num">Crédit</th></tr>
                  </thead>
                  <tbody>
                    {e.lines.map((l) => (
                      <tr key={l.lineNo}>
                        <td>{l.account}</td>
                        <td>{l.accountLabel}</td>
                        <td>{l.holderRef ?? ''}</td>
                        <td className="fin-num">{l.debitCents === 0 ? '' : formatAmount(l.debitCents)}</td>
                        <td className="fin-num">{l.creditCents === 0 ? '' : formatAmount(l.creditCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <p className="muted" data-testid="fin-document-count">
            {doc.entries.length} sur {doc.entriesTotal} écriture(s)
            {doc.entries.length < doc.entriesTotal && ' : les plus anciennes seulement, la liste est plafonnée.'}
          </p>
        </>
      )}

      <h5>Mouvements Vega portant ce numéro</h5>
      {doc.vegaPayments.length === 0 ? (
        <p className="muted" data-testid="fin-document-no-vega">Aucun mouvement Vega ne porte exactement ce numéro.</p>
      ) : (
        <div className="pms-table-wrap">
          <table className="pms-table" data-testid="fin-document-vega">
            <thead>
              <tr><th>Date</th><th>Type</th><th>Mode</th><th>Nom</th><th className="fin-num">Montant</th></tr>
            </thead>
            <tbody>
              {doc.vegaPayments.map((v, i) => (
                <tr key={`${v.kind}|${v.reference}|${i}`}>
                  <td>{v.movementDate ? formatDay(v.movementDate) : ''}</td>
                  <td>{v.kindLabel ?? v.kind}</td>
                  <td>{v.method ?? ''}</td>
                  <td>{v.name ?? ''}</td>
                  <td className="fin-num">{money(v.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── the container ───────────────────────────────────────────────────────────

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; doc: DocumentDetail }
  | { phase: 'forbidden' }
  | { phase: 'error'; message: string };

export function Document({ ledger, book, piece, onClose }: { ledger: LedgerReader; book: string; piece: string; onClose: () => void }) {
  const [state, setState] = useState<State>({ phase: 'loading' });

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    const r = await ledger.document(book, piece);
    if (r.ok) setState({ phase: 'ready', doc: r.value });
    else if (r.error.kind === 'forbidden') setState({ phase: 'forbidden' });
    else setState({ phase: 'error', message: r.error.message });
  }, [ledger, book, piece]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.phase === 'loading') return <p className="muted" data-testid="fin-document-loading">Chargement de la pièce {piece}…</p>;
  if (state.phase === 'forbidden') {
    return (
      <div className="pms-warn" role="alert" data-testid="fin-document-forbidden">
        Droit manquant : le détail d’une pièce est réservé à l’administrateur.
        <button type="button" onClick={onClose}>Fermer</button>
      </div>
    );
  }
  if (state.phase === 'error') {
    return (
      <div className="pms-warn" role="alert" data-testid="fin-document-error">
        <p>La pièce {piece} est illisible : ne rien en conclure. ({state.message})</p>
        <button type="button" onClick={() => void load()}>Réessayer</button>
        <button type="button" onClick={onClose}>Fermer</button>
      </div>
    );
  }
  return <DocumentView doc={state.doc} onClose={onClose} />;
}
